import TextArea from "../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import React, { useState, useEffect } from "react";
import AIAContent from "./AIAContent";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import DrawHistory from "./DrawHistory";
import BankProgressSection from "./BankProgressSection";
import ContactsSection from "./ContactsSection";
import PreconSection from "./PreconSection";
import DocumentsSection from "./DocumentsSection";
import ChangeOrdersSection from "./ChangeOrdersSection";
import PreconChecklistSection from "Pages/Admin/ProjectDetail/sections/PreconChecklistSection";
import CSCContactSection from "./CSCContactSection";
import { ProjectDetail } from "../BankDashboard/BankDashboard";
import { formatCurrency } from "Pages/GcPrecon/GcProjectDetails/components/sections/SOVSetup/constants/sovSetupData";
import { useAiaPdf } from "hooks/useAiaPdf";
import { useProgressReports } from "hooks/useProgressReports";
import { formatDate } from "utils/Common/Date";
import Button from "Components/Common/Button/Button";
import {
  getLenderPendingDraw,
  lenderDrawAction,
  lenderSovAction,
  getLenderDrawComments,
  postLenderDrawComment,
  getLenderPreconReview,
} from "services/lenderService";
import { getLenderPreconChecklist } from "services/projectService";
import { downloadDocumentBlob } from "services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import { useToast } from "Components/Common/Toast/ToastContext";
import MessageIcon from "Assets/Img/MessageIcon";
import ApproveTickIcon from "Assets/Img/ApproveTickIcon";
import CrossIcon from "Assets/Img/CrossIcon";
import DownloadIcon from "Assets/Img/DownloadIcon";

type ModalType = "approve" | "reject" | "comment";

interface AmountField {
  amount: number;
  currency: { code: string; name: string; symbol: string };
}

interface PendingDrawData {
  project: {
    id: string;
    name: string;
    type: string;
    progress: number;
    gcCompany: string;
    startDate: string | null;
    contractDate: string | null;
    estimatedCompletionDate: string | null;
  };
  draw: {
    id: string;
    drawNumber: number;
    toDate: string;
    drawPercentage: number;
    lienWaivers: {
      totalLienWaivers: number;
      totalSigned: number;
      totalInvoice: number;
    };
    g702: {
      retainageRate: string;
      totalRetainage: AmountField;
      balanceToFinish: AmountField;
      contractSumToDate: AmountField;
      currentPaymentDue: AmountField;
      originalContractSum: AmountField;
      totalCompletedAndStored: AmountField;
      lessPreviousCertificates: AmountField;
      totalEarnedLessRetainage: AmountField;
    };
  };
  report: {
    reportNumber: number;
    date: string;
    overallCscEstimation: string;
    flags: string[];
    bankToVerifyItem: { inspectorName: string };
  };
}

interface PreconReviewData {
  reportDate: string;
  preconstructionReportId?: string;
  project: {
    name: string;
    type: string;
    ownerCompany: string;
    gcCompany: string;
  };
  hardCostBudget: {
    amount: number;
    currency: { code: string; symbol: string; name: string };
  };
  estCompletionDate: string;
}

interface CommentItem {
  id: string;
  body: string;
  createdAt: string;
  author: {
    firstName: string;
    lastName: string;
    companyName: string;
    lenderBranch?: { companyName: string; branchName: string };
  };
}

export interface LenderOverviewData {
  name?: string;
  contractDate?: string | null;
  estCompletionDate?: string | null;
  drawRequest?: { id?: string; drawNumber?: number };
  g702?: {
    contractSumToDate?: AmountField;
    totalCompletedAndStored?: AmountField;
    retainageRate?: string;
    totalRetainage?: AmountField;
    totalEarnedLessRetainage?: AmountField;
    lessPreviousCertificates?: AmountField;
    currentPaymentDue?: AmountField;
    balanceToFinish?: AmountField;
  };
  report?: { id: string; reportNumber: number; reportDate: string };
  stakeholders?: {
    gc?: { companyName?: string };
    inspectors?: Array<{
      firstName?: string;
      lastName?: string;
      companyName?: string;
    }>;
  };
}

interface DrawSectionProps {
  activeSection: string;
  setOpenModal: (modal: ModalType | null) => void;
  expandedDraw: string | null;
  setExpandedDraw: (draw: string | null) => void;
  projectId?: string;
  projectDetail?: ProjectDetail | null;
  projectStage?: string;
  fetchProjectDetail?: () => void;
  overviewData?: LenderOverviewData | null;
}

const formatAmount = (amount: number): string =>
  `$${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;

const formatCommentDate = (iso: string): string => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) +
    " at " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
};

export default function DrawSection({
  activeSection,
  setOpenModal,
  expandedDraw,
  setExpandedDraw,
  projectId,
  projectDetail,
  fetchProjectDetail,
  overviewData = null,
}: DrawSectionProps) {
  const { exportAiaPdf, isExportingPdf } = useAiaPdf();
  const [pendingDraw, setPendingDraw] = useState<PendingDrawData | null>(null);
  const fetchedRef = React.useRef(false);
  const showToast = useToast();
  const [drawAction, setDrawAction] = useState<"approved" | "rejected" | null>(
    null,
  );
  const [drawActionNote, setDrawActionNote] = useState("");
  const [drawActionSubmitting, setDrawActionSubmitting] = useState(false);
  const [drawActionDone, setDrawActionDone] = useState<
    "approved" | "rejected" | null
  >(null);

  const [comments, setComments] = useState<CommentItem[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [preconData, setPreconData] = useState<PreconReviewData | null>(null);
  const [preconLoading, setPreconLoading] = useState(false);
  const [preconNotFound, setPreconNotFound] = useState(false);
  const [checklistData, setChecklistData] = useState<any>(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistNotFound, setChecklistNotFound] = useState(false);
  const [downloadingAllReports, setDownloadingAllReports] = useState(false);

  const { reports } = useProgressReports(projectId ?? "");

  useEffect(() => {
    if (!projectId || activeSection !== "precon-checklist") return;
    setChecklistLoading(true);
    (getLenderPreconChecklist(projectId) as Promise<any>).then((res: any) => {
      if (!res.error && res.data?.data) setChecklistData(res.data.data);
      if (res.error?.status === 404) setChecklistNotFound(true);
      setChecklistLoading(false);
    });
  }, [projectId, activeSection]);

  useEffect(() => {
    if (!projectId || activeSection !== "precon" || preconData) return;
    setPreconLoading(true);
    (getLenderPreconReview(projectId, true) as Promise<any>).then(
      (res: any) => {
        if (!res.error && res.data?.data) setPreconData(res.data.data);
        if (res.error?.status === 404) setPreconNotFound(true);
        setPreconLoading(false);
      },
    );
  }, [projectId, activeSection]);
  const [loading, setLoading] = useState(() => {
    const s = projectDetail?.projectStage;
    return s !== "lender_pre_construction_action";
  });

  const stage = projectDetail?.projectStage;

  useEffect(() => {
    if (!projectId || !stage || fetchedRef.current) return;
    fetchedRef.current = true;

    if (stage === "lender_pre_construction_action") {
      setLoading(false);
      return;
    }

    setLoading(true);

    if (stage === "lender_draw_request_action") {
      setDrawActionDone(null);
      (getLenderPendingDraw(projectId) as Promise<any>).then((res: any) => {
        if (!res.error && res.data?.data) {
          setPendingDraw(res.data.data);
          if (res.data.data?.draw?.id) loadComments(res.data.data.draw.id);
        }
        setLoading(false);
      });
    } else {
      const drawId = overviewData?.drawRequest?.id;
      if (drawId) loadComments(drawId);
      setLoading(false);
    }
  }, [projectId, stage]);

  const loadComments = async (drawId: string) => {
    setCommentsLoading(true);
    const res = await (getLenderDrawComments(
      projectId!,
      drawId,
    ) as Promise<any>);
    if (!res.error && res.data?.data) setComments(res.data.data);
    setCommentsLoading(false);
  };

  const handleDrawAction = async () => {
    if (!projectId || !drawAction) return;
    if (drawAction === "rejected" && !drawActionNote.trim()) {
      showToast("Please provide a reason for rejection.");
      return;
    }
    setDrawActionSubmitting(true);
    let res: any;
    if (stage === "lender_pre_construction_action") {
      res = await (lenderSovAction(projectId, {
        lenderAction: drawAction,
        note: drawActionNote,
      }) as Promise<any>);
    } else {
      if (!pendingDraw?.draw?.id) {
        setDrawActionSubmitting(false);
        return;
      }
      res = await (lenderDrawAction(projectId, pendingDraw.draw.id, {
        action: drawAction,
        lenderNote: drawActionNote,
      }) as Promise<any>);
    }
    setDrawActionSubmitting(false);
    if (res.error) {
      showToast(res.error.message || `Failed to ${drawAction}.`);
    } else {
      showToast(
        stage === "lender_pre_construction_action"
          ? `SOV ${drawAction} successfully.`
          : `Draw ${drawAction} successfully.`,
      );
      setDrawActionDone(drawAction);
      fetchProjectDetail && fetchProjectDetail();
    }
    setDrawAction(null);
    setDrawActionNote("");
  };

  const handlePostComment = async () => {
    const drawId = pendingDraw?.draw?.id ?? overviewData?.drawRequest?.id;

    if (!projectId || !drawId || !newComment.trim()) return;
    setPostingComment(true);
    const res = await (postLenderDrawComment(
      projectId,
      drawId,
      newComment,
    ) as Promise<any>);
    setPostingComment(false);
    if (res.error) {
      showToast(res.error.message || "Failed to post comment.");
    } else {
      setNewComment("");
      setShowCommentModal(false);
      showToast("Comment posted.");
      loadComments(drawId);
    }
  };

  const handleDownloadAllReports = async () => {
    if (!projectId || !reports.length) return;
    setDownloadingAllReports(true);
    try {
      let successCount = 0;
      let failedCount = 0;
      const failedReports: string[] = [];

      const downloadPromises = reports.map(async (report) => {
        const blob = await downloadDocumentBlob(projectId, report.id, true);
        if (blob) {
          triggerBlobDownload(
            blob,
            `ProgressReport-${report.reportNumber}.pdf`,
          );
          successCount++;
        } else {
          failedCount++;
          failedReports.push(`Report #${report.reportNumber}`);
        }
      });
      await Promise.all(downloadPromises);

      if (failedCount === 0) {
        showToast(`All ${successCount} reports downloaded successfully.`);
      } else if (successCount === 0) {
        showToast(
          `Failed to download ${failedCount} reports. Please try again.`,
        );
      } else {
        showToast(
          `Downloaded ${successCount} of ${reports.length} reports. Failed: ${failedReports.join(", ")}`,
        );
      }
    } catch (error) {
      showToast("Error downloading reports. Please try again.");
    } finally {
      setDownloadingAllReports(false);
    }
  };

  const [downloadingReport, setDownloadingReport] = useState(false);
  const handleDownloadReport = async () => {
    const reportId = overviewData?.report?.id;
    if (!projectId || !reportId) return;
    setDownloadingReport(true);
    try {
      const blob = await downloadDocumentBlob(projectId, reportId, true);
      if (blob) {
        const reportNum = overviewData?.report?.reportNumber ?? "report";
        triggerBlobDownload(blob, `ProgressReport-${reportNum}.pdf`);
      } else {
        showToast("Failed to download report. Please try again.");
      }
    } catch {
      showToast("Error downloading report. Please try again.");
    } finally {
      setDownloadingReport(false);
    }
  };

  if (activeSection === "overview") {
    if (
      stage !== "lender_pre_construction_action" &&
      stage !== "lender_draw_request_action"
    ) {
      const g702 = overviewData?.g702;
      const inspector = overviewData?.stakeholders?.inspectors?.[0];
      return (
        <div className="ds active">
          <div className="dsHeader">
            <div className="dsHeaderTitle">Overview</div>
          </div>

          {loading ? (
            <div className="two-col">
              <div className="card">
                <Skeleton
                  width="40%"
                  height={14}
                  style={{ marginBottom: 12 }}
                />
                <Skeleton width="100%" height={140} />
              </div>
              <div className="card">
                <Skeleton
                  width="40%"
                  height={14}
                  style={{ marginBottom: 12 }}
                />
                <Skeleton width="100%" height={90} />
              </div>
            </div>
          ) : (
            <>
              <div className="two-col">
                <div className="card">
                  <div className="card-title">Payment Summary</div>
                  {g702 ? (
                    <>
                      <div className="aia-line">
                        <span>
                          <span className="aia-lnum">3</span>
                          <span className="aia-label">
                            Contract sum to date
                          </span>
                        </span>
                        <span className="aia-val">
                          {g702.contractSumToDate?.amount != null
                            ? formatAmount(g702.contractSumToDate.amount)
                            : "—"}
                        </span>
                      </div>
                      <div className="aia-line">
                        <span>
                          <span className="aia-lnum">4</span>
                          <span className="aia-label">
                            Total completed &amp; stored
                          </span>
                        </span>
                        <span className="aia-val">
                          {g702.totalCompletedAndStored?.amount != null
                            ? formatAmount(g702.totalCompletedAndStored.amount)
                            : "—"}
                        </span>
                      </div>
                      <div className="aia-line">
                        <span>
                          <span className="aia-lnum">5</span>
                          <span className="aia-label">
                            Retainage ({g702.retainageRate ?? "—"}%)
                          </span>
                        </span>
                        <span className="aia-val">
                          {g702.totalRetainage?.amount != null
                            ? formatAmount(g702.totalRetainage.amount)
                            : "—"}
                        </span>
                      </div>
                      <div className="aia-line">
                        <span>
                          <span className="aia-lnum">6</span>
                          <span className="aia-label">
                            Earned less retainage
                          </span>
                        </span>
                        <span className="aia-val blue">
                          {g702.totalEarnedLessRetainage?.amount != null
                            ? formatAmount(g702.totalEarnedLessRetainage.amount)
                            : "—"}
                        </span>
                      </div>
                      <div className="aia-line">
                        <span>
                          <span className="aia-lnum">7</span>
                          <span className="aia-label">
                            Less prior certificates
                          </span>
                        </span>
                        <span className="aia-val">
                          {g702.lessPreviousCertificates?.amount != null
                            ? formatAmount(g702.lessPreviousCertificates.amount)
                            : "—"}
                        </span>
                      </div>
                      <div className="aia-line tl">
                        <span>
                          <span className="aia-lnum">8</span>
                          <span className="aia-label aiaLabelBold">
                            Current payment due
                          </span>
                        </span>
                        <span className="aia-val green">
                          {g702.currentPaymentDue?.amount != null
                            ? formatAmount(g702.currentPaymentDue.amount)
                            : "—"}
                        </span>
                      </div>
                      <div className="aia-line">
                        <span>
                          <span className="aia-lnum">9</span>
                          <span className="aia-label">Balance to finish</span>
                        </span>
                        <span className="aia-val">
                          {g702.balanceToFinish?.amount != null
                            ? formatAmount(g702.balanceToFinish.amount)
                            : "—"}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="da-sub">No payment data available.</div>
                  )}
                </div>

                <div className="card">
                  <div className="card-title">Schedule &amp; Notes</div>
                  <div className="scheduleNotes">
                    <div className="scheduleRow">
                      Contract date:{" "}
                      <strong>
                        {overviewData?.contractDate
                          ? formatDate(overviewData.contractDate, false)
                          : "—"}
                      </strong>
                    </div>
                    <div className="scheduleRow">
                      Est. completion:{" "}
                      <strong>
                        {overviewData?.estCompletionDate
                          ? formatDate(overviewData.estCompletionDate, false)
                          : "—"}
                      </strong>
                    </div>
                    {inspector && (
                      <div className="scheduleMeta">
                        Inspector:{" "}
                        <strong>
                          {[inspector.firstName, inspector.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </strong>
                      </div>
                    )}
                    {overviewData?.report && (
                      <div className="scheduleMeta">
                        Latest report:{" "}
                        <strong>
                          Report {overviewData.report.reportNumber}
                        </strong>{" "}
                        · {formatDate(overviewData.report.reportDate, false)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {(pendingDraw?.draw?.id ?? overviewData?.drawRequest?.id) && (
                <div className="card">
                  <div className="card-title">
                    Lender Comments
                    <Button
                      className="btn"
                      onClick={() => setShowCommentModal(true)}
                    >
                      + Add
                    </Button>
                  </div>
                  {commentsLoading ? (
                    [1, 2].map((n) => (
                      <div className="comment-item" key={n}>
                        <Skeleton width={36} height={36} borderRadius={18} />
                        <div className="comment-body" style={{ flex: 1 }}>
                          <Skeleton
                            width="50%"
                            height={11}
                            style={{ marginBottom: 6 }}
                          />
                          <Skeleton width="90%" height={11} />
                        </div>
                      </div>
                    ))
                  ) : comments.length > 0 ? (
                    comments.map((c) => {
                      const initials =
                        `${c.author.firstName[0] ?? ""}${c.author.lastName[0] ?? ""}`.toUpperCase();
                      const branch =
                        c.author.lenderBranch?.companyName ??
                        c.author.companyName;
                      return (
                        <div className="comment-item" key={c.id}>
                          <div className="comment-av">{initials}</div>
                          <div className="comment-body">
                            <div className="comment-meta">
                              {c.author.firstName} {c.author.lastName} ·{" "}
                              {branch} · {formatCommentDate(c.createdAt)}
                            </div>
                            <div className="comment-text">{c.body}</div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="da-sub" style={{ padding: "8px 0" }}>
                      No comments yet.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <CustomModal
            show={showCommentModal}
            handleClose={() => {
              setShowCommentModal(false);
              setNewComment("");
            }}
            title="Add Comment"
            isHeaderVisible={true}
            isCrossBtn={false}
            isFooterVisible={true}
            footerContent={
              <>
                <Button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCommentModal(false);
                    setNewComment("");
                  }}
                  disabled={postingComment}
                >
                  Cancel
                </Button>
                <Button
                  className="approve-btn"
                  onClick={handlePostComment}
                  disabled={
                    postingComment ||
                    !newComment.trim() ||
                    newComment.length > CHAR_LIMIT.DESCRIPTION
                  }
                >
                  {postingComment ? "Posting…" : "Post Comment"}
                </Button>
              </>
            }
          >
            <p className="modal-sub">
              Your comment will be visible to CSC New England and logged against
              this project.
            </p>
            <TextArea
              className="form-control mt-3"
              placeholder="Write your comment…"
              rows={4}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              disabled={postingComment}
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
          </CustomModal>
        </div>
      );
    }

    if (loading && stage === "lender_draw_request_action") {
      return (
        <div className="ds active">
          <div className="dsHeader">
            <Skeleton width="30%" height={18} />
            <Skeleton width={130} height={34} borderRadius={6} />
          </div>
          <div className="draw-action">
            <div className="da-header">
              <div>
                <Skeleton width="35%" height={15} style={{ marginBottom: 6 }} />
                <Skeleton width="55%" height={11} />
              </div>
            </div>
            <div className="da-grid">
              {[1, 2, 3].map((n) => (
                <div className="da-item" key={n}>
                  <Skeleton
                    width="60%"
                    height={11}
                    style={{ marginBottom: 6 }}
                  />
                  <Skeleton width="40%" height={18} />
                </div>
              ))}
            </div>
            <div className="daBtnGroup">
              <Skeleton width={130} height={36} borderRadius={6} />
              <Skeleton width={120} height={36} borderRadius={6} />
            </div>
          </div>
          {stage === "lender_draw_request_action" && (
            <>
              <div className="two-col">
                <div className="card">
                  <Skeleton
                    width="40%"
                    height={14}
                    style={{ marginBottom: 12 }}
                  />
                  <Skeleton width="100%" height={140} />
                </div>
                <div className="card">
                  <Skeleton
                    width="40%"
                    height={14}
                    style={{ marginBottom: 12 }}
                  />
                  <Skeleton width="100%" height={90} />
                </div>
              </div>
              <div className="card">
                <Skeleton
                  width="30%"
                  height={14}
                  style={{ marginBottom: 12 }}
                />
                <Skeleton width="100%" height={60} />
              </div>
            </>
          )}
        </div>
      );
    }

    const g702 = pendingDraw?.draw?.g702;
    const draw = pendingDraw?.draw;
    const report = pendingDraw?.report;
    const proj = pendingDraw?.project;
    const preconItem = projectDetail?.lenderPreConstructionActionItem;
    const gcCompany = projectDetail?.stakeholders?.gc?.companyName;
    const actionModal = (
      <CustomModal
        show={drawAction !== null}
        handleClose={() => {
          setDrawAction(null);
          setDrawActionNote("");
        }}
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        title={
          drawAction === "approved"
            ? stage === "lender_pre_construction_action"
              ? "Approve Precon?"
              : `Approve Draw${draw?.drawNumber != null ? ` #${String(draw.drawNumber).padStart(2, "0")}` : ""}?`
            : stage === "lender_pre_construction_action"
              ? "Reject Precon?"
              : `Reject Draw${draw?.drawNumber != null ? ` #${String(draw.drawNumber).padStart(2, "0")}` : ""}?`
        }
        footerContent={
          <>
            <Button
              className="btn btn-secondary"
              onClick={() => {
                setDrawAction(null);
                setDrawActionNote("");
              }}
              disabled={drawActionSubmitting}
            >
              Cancel
            </Button>
            <Button
              className={
                drawAction === "approved" ? "approve-btn" : "reject-btn"
              }
              onClick={handleDrawAction}
              disabled={
                drawActionSubmitting ||
                drawActionNote.length > CHAR_LIMIT.DESCRIPTION
              }
            >
              {drawActionSubmitting
                ? "Submitting…"
                : drawAction === "approved"
                  ? "Confirm Approval"
                  : "Confirm Rejection"}
            </Button>
          </>
        }
      >
        <p>
          {stage === "lender_pre_construction_action" ? (
            drawAction === "approved" ? (
              <>
                You are about to approve the Schedule of Values (SOV) for{" "}
                <strong>{projectDetail?.name ?? "this project"}</strong>. Add an
                optional note for the record:
              </>
            ) : (
              <>
                Provide a reason for rejecting the SOV. Your note will be shared
                with CSC New England and the GC.
              </>
            )
          ) : drawAction === "approved" ? (
            <>
              Approving Draw #
              {draw?.drawNumber != null
                ? String(draw.drawNumber).padStart(2, "0")
                : "—"}
              {g702?.currentPaymentDue?.amount != null && (
                <>
                  {" "}
                  for{" "}
                  <strong>{formatAmount(g702.currentPaymentDue.amount)}</strong>
                </>
              )}
              {proj?.name && <> on {proj.name}</>}.{" "}
              {report?.flags?.length ? (
                <>
                  Note: CSC flagged {report.flags.join(" and ")} as potentially
                  overbilled.{" "}
                </>
              ) : null}
              Add an optional note for the record:
            </>
          ) : (
            <>
              Provide a reason for rejecting this draw. Your note will be shared
              with CSC New England and the GC.
            </>
          )}
        </p>
        <TextArea
          className="form-control mt-3"
          placeholder={
            drawAction === "approved"
              ? "e.g. Approved pending GC clarification on electrical line item…"
              : "e.g. Holding pending resolution of overbilling flag…"
          }
          rows={4}
          value={drawActionNote}
          onChange={(e) => setDrawActionNote(e.target.value)}
          disabled={drawActionSubmitting}
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
      </CustomModal>
    );

    if (stage == "lender_pre_construction_action") {
      return (
        <div className="ds active">
          <div className="dsHeader">
            <div className="dsHeaderTitle">Overview</div>
          </div>

          <div className="draw-action">
            <div className="da-header">
              <div>
                <div className="da-title">Lender Approval</div>
                <div className="da-sub">
                  {gcCompany || "—"} · Est. Date{" "}
                  {preconItem?.estCompletionDate
                    ? formatDate(preconItem.estCompletionDate, false)
                    : "—"}
                </div>
              </div>
            </div>

            <div className="da-grid">
              <div className="da-item">
                <div className="da-item-label">Contract Amount</div>
                <div className="da-item-val ok">
                  {preconItem?.contractAmount?.amount != null
                    ? formatCurrency(preconItem.contractAmount.amount)
                    : "—"}
                </div>
              </div>
              <div className="da-item">
                <div className="da-item-label">SOV Total</div>
                <div className="da-item-val ok">
                  {preconItem?.sovTotal?.amount != null
                    ? formatCurrency(preconItem.sovTotal.amount)
                    : "—"}
                </div>
              </div>
              <div className="da-item">
                <div className="da-item-label">Total Lien Items</div>
                <div className="da-item-val">
                  {preconItem?.sovLienCount ?? "—"}
                </div>
              </div>
            </div>

            <div className="daBtnGroup">
              {drawActionDone ? (
                <Button
                  className={
                    drawActionDone === "approved" ? "approve-btn" : "reject-btn"
                  }
                  disabled
                >
                  {drawActionDone === "approved" ? "✓ Approved" : "✗ Rejected"}
                </Button>
              ) : (
                <>
                  <Button
                    className="approve-btn"
                    onClick={() => setDrawAction("approved")}
                  >
                    <ApproveTickIcon /> Approve Precon
                  </Button>
                  <Button
                    className="reject-btn"
                    onClick={() => setDrawAction("rejected")}
                  >
                    <CrossIcon /> Reject Precon
                  </Button>
                </>
              )}
            </div>
          </div>

          {actionModal}
        </div>
      );
    }

    // lender_draw_request_action
    return (
      <div className="ds active">
        <div className="dsHeader">
          <div className="dsHeaderTitle">Overview</div>
        </div>

        <div className="draw-action">
          <div className="da-header">
            <div>
              <div className="da-title">
                Draw #
                {draw?.drawNumber != null
                  ? String(draw.drawNumber).padStart(2, "0")
                  : "—"}{" "}
                — Pending Your Approval
              </div>
              <div className="da-sub">
                {proj?.gcCompany || "—"} · Period to{" "}
                {draw?.toDate ? formatDate(draw.toDate, false) : "—"}
              </div>
            </div>
            <div className="daHeaderRight">
              <div className="da-amt-label">Amount Requested</div>
              <div className="da-amount">
                {g702?.currentPaymentDue?.amount != null
                  ? formatAmount(g702.currentPaymentDue.amount)
                  : "—"}
              </div>
            </div>
          </div>

          <div className="da-grid">
            <div className="da-item">
              <div className="da-item-label">GC Billed</div>
              <div className="da-item-val warn">
                {draw?.drawPercentage != null
                  ? `${draw.drawPercentage.toFixed(2)}%`
                  : "—"}
              </div>
            </div>
            <div className="da-item">
              <div className="da-item-label">CSC Estimate</div>
              <div className="da-item-val warn">
                {report?.overallCscEstimation != null
                  ? `${report.overallCscEstimation}%`
                  : "—"}
              </div>
            </div>
            <div className="da-item">
              <div className="da-item-label">Retainage Held</div>
              <div className="da-item-val">
                {g702?.totalRetainage?.amount != null
                  ? formatAmount(g702.totalRetainage.amount)
                  : "—"}
              </div>
            </div>
            <div className="da-item">
              <div className="da-item-label">Lien Waivers</div>
              <div className="da-item-val warn">
                {draw?.lienWaivers
                  ? `${draw.lienWaivers.totalSigned} of ${draw.lienWaivers.totalLienWaivers} signed`
                  : "—"}
              </div>
            </div>
            <div className="da-item">
              <div className="da-item-label">Invoices</div>
              <div className="da-item-val ok">
                {draw?.lienWaivers?.totalInvoice != null
                  ? `${draw.lienWaivers.totalInvoice} attached`
                  : "—"}
              </div>
            </div>
            <div className="da-item">
              <div className="da-item-label">Balance to Finish</div>
              <div className="da-item-val">
                {g702?.balanceToFinish?.amount != null
                  ? formatAmount(g702.balanceToFinish.amount)
                  : "—"}
              </div>
            </div>
          </div>

          {report?.flags?.length ? (
            <div className="flag-box">
              <strong>
                ⚠ CSC Overbilling Flags — {report.flags.length} Item
                {report.flags.length !== 1 ? "s" : ""}
              </strong>{" "}
              {report.flags.join(" · ")}
            </div>
          ) : null}

          <div className="daBtnGroup">
            {drawActionDone ? (
              <Button
                className={
                  drawActionDone === "approved" ? "approve-btn" : "reject-btn"
                }
                disabled
              >
                {drawActionDone === "approved" ? "✓ Approved" : "✗ Rejected"}
              </Button>
            ) : (
              <>
                <Button
                  className="approve-btn"
                  onClick={() => setDrawAction("approved")}
                >
                  <ApproveTickIcon /> Approve Draw
                </Button>
                <Button
                  className="reject-btn"
                  onClick={() => setDrawAction("rejected")}
                >
                  <CrossIcon /> Reject Draw
                </Button>
                <Button
                  className="btn"
                  onClick={handleDownloadReport}
                  disabled={downloadingReport || !overviewData?.report?.id}
                >
                  {downloadingReport ? (
                    <span className="btn-spinner" />
                  ) : (
                    <DownloadIcon />
                  )}
                  Download Report
                </Button>
                <Button
                  className="btn"
                  onClick={() => setShowCommentModal(true)}
                >
                  <MessageIcon />
                  Add Comment
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="two-col">
          <div className="card">
            <div className="card-title">Payment Summary</div>
            {g702 ? (
              <>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">3</span>
                    <span className="aia-label">Contract sum to date</span>
                  </span>
                  <span className="aia-val">
                    {formatAmount(g702.contractSumToDate.amount)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">4</span>
                    <span className="aia-label">
                      Total completed &amp; stored
                    </span>
                  </span>
                  <span className="aia-val">
                    {formatAmount(g702.totalCompletedAndStored.amount)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">5</span>
                    <span className="aia-label">
                      Retainage ({g702.retainageRate}%)
                    </span>
                  </span>
                  <span className="aia-val">
                    {formatAmount(g702.totalRetainage.amount)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">6</span>
                    <span className="aia-label">Earned less retainage</span>
                  </span>
                  <span className="aia-val blue">
                    {formatAmount(g702.totalEarnedLessRetainage.amount)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">7</span>
                    <span className="aia-label">Less prior certificates</span>
                  </span>
                  <span className="aia-val">
                    {formatAmount(g702.lessPreviousCertificates.amount)}
                  </span>
                </div>
                <div className="aia-line tl">
                  <span>
                    <span className="aia-lnum">8</span>
                    <span className="aia-label aiaLabelBold">
                      Current payment due
                    </span>
                  </span>
                  <span className="aia-val green">
                    {formatAmount(g702.currentPaymentDue.amount)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">9</span>
                    <span className="aia-label">Balance to finish</span>
                  </span>
                  <span className="aia-val">
                    {formatAmount(g702.balanceToFinish.amount)}
                  </span>
                </div>
              </>
            ) : (
              <div className="da-sub">No payment data available.</div>
            )}
          </div>

          <div className="card">
            <div className="card-title">Schedule &amp; Notes</div>
            <div className="scheduleNotes">
              <div className="scheduleRow">
                Contract date:{" "}
                <strong>
                  {proj?.contractDate
                    ? formatDate(proj.contractDate, false)
                    : "—"}
                </strong>
              </div>
              <div className="scheduleRow">
                Est. completion:{" "}
                <strong>
                  {proj?.estimatedCompletionDate
                    ? formatDate(proj.estimatedCompletionDate, false)
                    : "—"}
                </strong>
              </div>
              {proj?.startDate && (
                <div className="scheduleRow">
                  Start date:{" "}
                  <strong>{formatDate(proj.startDate, false)}</strong>
                </div>
              )}
              {report?.bankToVerifyItem?.inspectorName && (
                <div className="scheduleMeta">
                  Inspector:{" "}
                  <strong>{report.bankToVerifyItem.inspectorName}</strong>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            Lender Comments
            <Button className="btn" onClick={() => setShowCommentModal(true)}>
              + Add
            </Button>
          </div>

          {commentsLoading ? (
            <>
              {[1, 2].map((n) => (
                <div className="comment-item" key={n}>
                  <Skeleton width={36} height={36} borderRadius={18} />
                  <div className="comment-body" style={{ flex: 1 }}>
                    <Skeleton
                      width="50%"
                      height={11}
                      style={{ marginBottom: 6 }}
                    />
                    <Skeleton width="90%" height={11} />
                  </div>
                </div>
              ))}
            </>
          ) : comments.length > 0 ? (
            comments.map((c) => {
              const initials =
                `${c.author.firstName[0] ?? ""}${c.author.lastName[0] ?? ""}`.toUpperCase();
              const branch =
                c.author.lenderBranch?.companyName ?? c.author.companyName;
              return (
                <div className="comment-item" key={c.id}>
                  <div className="comment-av">{initials}</div>
                  <div className="comment-body">
                    <div className="comment-meta">
                      {c.author.firstName} {c.author.lastName} · {branch} ·{" "}
                      {formatCommentDate(c.createdAt)}
                    </div>
                    <div className="comment-text">{c.body}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="da-sub" style={{ padding: "8px 0" }}>
              No comments yet.
            </div>
          )}
        </div>

        <CustomModal
          show={showCommentModal}
          handleClose={() => {
            setShowCommentModal(false);
            setNewComment("");
          }}
          title="Add Comment"
          isHeaderVisible={true}
          isCrossBtn={false}
          isFooterVisible={true}
          footerContent={
            <>
              <Button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCommentModal(false);
                  setNewComment("");
                }}
                disabled={postingComment}
              >
                Cancel
              </Button>
              <Button
                className="approve-btn"
                onClick={handlePostComment}
                disabled={
                  postingComment ||
                  !newComment.trim() ||
                  newComment.length > CHAR_LIMIT.DESCRIPTION
                }
              >
                {postingComment ? "Posting…" : "Post Comment"}
              </Button>
            </>
          }
        >
          <p className="modal-sub">
            Your comment will be visible to CSC New England and logged against
            this project.
          </p>
          <TextArea
            className="form-control mt-3"
            placeholder="Write your comment…"
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={postingComment}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
        </CustomModal>

        {actionModal}
      </div>
    );
  }

  if (activeSection === "aia") {
    return (
      <div className="ds active">
        <div className="dsHeader">
          <div className="dsHeaderTitle">AIA Summary — G702 / G703</div>
          <Button
            className="btn btn-sm"
            onClick={() =>
              exportAiaPdf(
                projectId ?? "",
                projectDetail?.name,
                projectDetail?.stakeholders?.lenderBranch?.companyName,
              )
            }
            disabled={isExportingPdf}
          >
            {isExportingPdf ? "Generating..." : "Download PDF"}
          </Button>
        </div>
        <div className="ro-notice">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="6" stroke="var(--tx3)" strokeWidth="1.2" />
            <line
              x1="7"
              y1="5.5"
              x2="7"
              y2="9"
              stroke="var(--tx3)"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
            <circle cx="7" cy="4" r=".6" fill="var(--tx3)" />
          </svg>
          AIA documents are prepared by CSC New England. Read-only in the lender
          portal.
        </div>
        <AIAContent projectId={projectId} />
      </div>
    );
  }

  if (activeSection === "draws") {
    return (
      <div className="ds active">
        <div className="dsHeader">
          <div className="dsHeaderTitle">Draw History</div>
          <Button
            className="btn btn-sm"
            onClick={() => showToast("Exported draw history")}
          >
            Export
          </Button>
        </div>
        <DrawHistory
          projectId={projectId}
          expandedDraw={expandedDraw}
          setExpandedDraw={setExpandedDraw}
          setOpenModal={setOpenModal}
          showToast={showToast}
        />
      </div>
    );
  }
  if (activeSection === "change-orders") {
    return <ChangeOrdersSection projectId={projectId ?? ""} />;
  }
  if (activeSection === "reports") {
    return (
      <div className="ds active">
        <div className="dsHeader">
          <div className="dsHeaderTitle">Progress Reports</div>
          <Button
            className="btn btn-sm"
            onClick={handleDownloadAllReports}
            disabled={downloadingAllReports || !reports.length}
          >
            {downloadingAllReports ? "Downloading..." : "Download All PDFs"}
          </Button>
        </div>
        <div className="ro-notice">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="6" stroke="var(--tx3)" strokeWidth="1.2" />
            <line
              x1="7"
              y1="5.5"
              x2="7"
              y2="9"
              stroke="var(--tx3)"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
            <circle cx="7" cy="4" r=".6" fill="var(--tx3)" />
          </svg>
          Reports prepared by CSC New England. Read-only.
        </div>
        <BankProgressSection projectId={projectId ?? ""} />
      </div>
    );
  }

  if (activeSection === "precon")
    return (
      <PreconSection
        data={preconData}
        loading={preconLoading}
        notFound={preconNotFound}
        projectId={projectId}
        preconReportId={preconData?.preconstructionReportId}
      />
    );
  if (activeSection === "precon-checklist")
    return (
      <PreconChecklistSection
        data={checklistData}
        loading={checklistLoading}
        notFound={checklistNotFound}
      />
    );
  if (activeSection === "contacts")
    return <ContactsSection projectId={projectId} />;
  if (activeSection === "documents")
    return <DocumentsSection projectId={projectId ?? ""} />;
  if (activeSection === "contact-csc") {
    return (
      <CSCContactSection
        setOpenModal={setOpenModal}
        showToast={showToast}
        projectId={projectId}
      />
    );
  }

  return null;
}
